import * as reactRouterDom from 'react-router-dom';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { EmailPasswordPreBuiltUI } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { getSuperTokensRoutesForReactRouterDom } from 'supertokens-auth-react/ui';

import { BidResponsePageLayout } from '../features/bidResponse/components/BidResponsePageLayout';
import { VaultSearchPageLayout } from '../features/bidResponse/components/vaultSearch/VaultSearchPageLayout';
import { StreamsProvider } from '../features/streams/contexts/Streams.provider';
import { Permission } from '../shared/entities/Access';
import { FeatureFlag } from '../shared/entities/FeatureFlags';
import { useFeatureFlag } from '../shared/hooks/useFeatureFlags.hook';
import { usePermission } from '../shared/hooks/usePermission.hook';
import { AppWithLogin } from './App';
import AssistantNewThreadPage from './pages/AssistantNewThreadPage.page';
import AssistantThreadPage from './pages/AssistantThreadPage.page';
import BuyerPage from './pages/Buyer.page';
import { BuyerSimDetailPage } from './pages/BuyerSimDetail.page';
import { BuyerSimulationPage } from './pages/BuyerSimulation.page';
import CreateStreamPage from './pages/CreateStreamPage';
import NoticePage from './pages/Notice.page';
import PageNotFound from './pages/PageNotFound';
import PipelinePage from './pages/Pipeline.page';
import { ResponseProjectPage } from './pages/ResponseProject.page';
import { ResponseProjectDetailPage } from './pages/ResponseProjectDetail.page';
import { VaultSearchNewPage } from './pages/VaultSearchNew.page';
import { VaultSearchResultsPage } from './pages/VaultSearchResults.page';
import StreamPage from './pages/Stream.page';
import SummarySheetSharePage from './pages/SummarySheetShare.page';
import UpdateStreamPage from './pages/UpdateStreamPage';
import { VaultPage } from './pages/Vault.page';
import { WorkspaceNotFound } from './pages/WorkspaceNotFound';
import { WorkspaceSettingsPage } from './pages/WorkspaceSettings.page';

export function Router() {
  return (
    <Routes>
      {getSuperTokensRoutesForReactRouterDom(reactRouterDom, [
        EmailPasswordPreBuiltUI,
      ])}

      {/* No login required */}
      {/* SHARED SUMMARY SHEET */}
      <Route
        path="/share/summary-sheet/:uuid"
        element={<SummarySheetSharePage />}
      />

      <Route
        path="/"
        element={
          <SessionAuth>
            <AppWithLogin />
          </SessionAuth>
        }
      >
        {/* HOME PAGE */}
        <Route path="/" element={<Navigate to="/flux" />} />

        {/* VIEWS/STREAM REDIRECTIONS PAGES */}
        <Route path="/views" element={<Navigate to="/flux" />} />
        <Route path="/views/:id" element={<RedirectWithParams to="/flux" />} />
        <Route path="/stream" element={<Navigate to="/flux" />} />
        <Route path="/stream/:id" element={<RedirectWithParams to="/flux" />} />

        {/* PIPELINE REDIRECTIONS PAGES */}
        <Route
          path="/tenders/:tabParam?"
          element={
            <RedirectWithParams to="/pipeline" paramNames={['tabParam']} />
          }
        />

        {/* STREAMS PAGES */}
        <Route element={<StreamsProvider />}>
          <Route
            path="/flux/create"
            element={
              <PermissionCheck permission={Permission.CREATE_STREAM}>
                <CreateStreamPage />
              </PermissionCheck>
            }
          />

          <Route
            path="/flux/:id/edit"
            element={
              <PermissionCheck permission={Permission.CREATE_STREAM}>
                <UpdateStreamPage />
              </PermissionCheck>
            }
          />

          <Route path="/flux" element={<StreamPage />} />
          <Route path="/flux/:id" element={<StreamPage />} />
        </Route>

        {/* BUYER PAGES */}
        <Route path="/buyer/:buyerId" element={<BuyerPage />} />

        {/* RESPONSE PAGES */}
        <Route path="/response" element={<BidResponsePageLayout />}>
          <Route index element={<Navigate to="/response/vault" replace />} />

          {/* VAULT */}
          <Route
            path="vault"
            element={
              <FeatureFlagRoute
                feature={FeatureFlag.BID_RESPONSE}
                fallbackPath="/response/simulation"
              />
            }
          >
            <Route path=":tabValue?" element={<VaultPage />} />
          </Route>

          {/* SEARCH */}
          <Route
            path="search"
            element={<FeatureFlagRoute feature={FeatureFlag.BID_RESPONSE} />}
          >
            <Route element={<VaultSearchPageLayout />}>
              <Route index element={<Navigate to="new" replace />} />
              <Route path="new" element={<VaultSearchNewPage />} />
              <Route path=":id?" element={<VaultSearchResultsPage />} />
            </Route>
          </Route>

          {/* ASSISTANT */}
          <Route
            path="assistant"
            element={<FeatureFlagRoute feature={FeatureFlag.BID_RESPONSE} />}
          >
            <Route index element={<AssistantNewThreadPage />} />
            <Route path=":id" element={<AssistantThreadPage />} />
          </Route>

          {/* BUYER SIMULATION */}
          <Route
            path="simulation"
            element={
              <FeatureFlagRoute feature={FeatureFlag.BUYER_SIMULATION} />
            }
          >
            <Route index element={<BuyerSimulationPage />} />
            <Route path=":id" element={<BuyerSimDetailPage />} />
          </Route>

          {/* RESPONSE PROJECTS */}
          <Route path="projects">
            <Route index element={<ResponseProjectPage />} />
            <Route path=":id" element={<ResponseProjectDetailPage />} />
          </Route>
        </Route>

        {/* NOTICES PAGES */}
        {/* tabParam is deprecated. Leaving it to ensure users are not redirected to a 404 */}
        <Route path="/notices/:id/:tabParam?" element={<NoticePage />} />

        {/* PIPELINE PAGES */}
        <Route path="/pipeline/:tabParam?" element={<PipelinePage />} />
        <Route path="/workspace-not-found" element={<WorkspaceNotFound />} />

        {/* WORKSPACE SETTINGS */}
        <Route path="/workspace-settings" element={<WorkspaceSettingsPage />} />

        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
}

const RedirectWithParams = ({
  to,
  query,
  paramNames = ['id'],
}: {
  to: string;
  query?: string;
  paramNames?: string[];
}) => {
  const params = useParams();
  const pathParams = paramNames.map(name => params[name]).join('/'); // Build path from params
  const queryString = query ? `?${query}` : '';

  return <Navigate to={`${to}/${pathParams}${queryString}`} replace />;
};

type FeatureFlagRouteProps = {
  feature: FeatureFlag;
  fallbackPath?: string;
};

function FeatureFlagRoute({
  feature,
  fallbackPath = '/',
}: FeatureFlagRouteProps) {
  const isFeatureEnabled = useFeatureFlag(feature);

  if (!isFeatureEnabled) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <Outlet />;
}

type PermissionRouteProps = {
  permission: Permission;
  fallbackPath?: string;
  children: React.ReactNode;
};

function PermissionCheck({
  permission,
  fallbackPath = '/',
  children,
}: PermissionRouteProps) {
  const { hasPermission } = usePermission();

  if (!hasPermission(permission)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}
